import { CURRENCY_MAX_DIGITS } from '../currency';
import { SKIN_SOCKET_PROFILE_ID, SKIN_SOCKET_PROFILE_VERSION } from './sockets';

/**
 * What a skin banner IS (ADR 0040), and the one the Closed Alpha ships
 * (ADR 0047).
 *
 * ## The shape, and one piece of content
 *
 * This file defines the SHAPE, and authors exactly one banner: the Closed Alpha
 * collection. Everything about it - price, pool, weights, socket profile,
 * duplicate policy - is decided in `SKIN_BANNERS` below, and the database row
 * the service reads is a projection of that constant rather than a second
 * configuration somebody typed twice.
 *
 * A definition may only appear in a banner's pool if it is finished — enabled,
 * with an asset and a VFX descriptor — which the three skins of that collection
 * now are. The server checks it anyway, every time, because a pool entry the
 * server would refuse to grant would be publishing a lie.
 *
 * ## The fingerprint, and what it is for
 *
 * A banner's configuration decides what a player is buying: the price, the
 * pool, the weights and the socket profile. A roll therefore carries the
 * fingerprint the player was SHOWN, and the server refuses the roll if its own
 * configuration no longer hashes to it.
 *
 * That is not idempotency and it does not replace it. It answers a different
 * question: "is this still the offer you agreed to?" A banner edited between
 * the moment the odds were displayed and the moment the button was pressed must
 * not silently charge the new price or draw from the new pool.
 *
 * The canonical SOURCE is built here, deterministically, and hashed with
 * SHA-256 by the server. It is built rather than hashed here because
 * `packages/shared` is platform-neutral and has no crypto: hashing lives where
 * Web Crypto does.
 */

/** Skins are bought with Cash. There is no Gold price and no second currency. */
export const SKIN_BANNER_CURRENCY = 'cash' as const;

export const SKIN_BANNER_STATUSES = ['draft', 'active', 'ended'] as const;
export type SkinBannerStatus = (typeof SKIN_BANNER_STATUSES)[number];

/**
 * What happens when a roll lands on a skin the account already owns.
 *
 * `new_instance` is the only policy this gate implements, and it is a
 * consequence of sockets rather than a choice about generosity: a second copy
 * has its own capacity, so it is a different item. The other values exist so
 * the column can hold a decision nobody has made yet, and no code produces
 * them.
 */
export const SKIN_DUPLICATE_POLICIES = ['new_instance'] as const;
export type SkinDuplicatePolicy = (typeof SKIN_DUPLICATE_POLICIES)[number];

/** One definition in a banner's pool, with its explicit weight. */
export interface SkinBannerPoolEntry {
  readonly definitionId: string;
  /**
   * Explicit and positive. Never derived, never defaulted and never equal for
   * everybody by omission: a pool where the weights were implied would publish
   * odds nobody chose.
   */
  readonly weight: number;
}

export interface SkinBannerDefinition {
  /** Stable across versions. `(id, version)` is what identifies a configuration. */
  readonly id: string;
  readonly version: number;
  readonly status: SkinBannerStatus;
  /** ISO-8601 UTC, or null for "no scheduled start / no scheduled end". */
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  /** Cash, as a decimal string. Never a JSON number: it is a currency amount. */
  readonly priceCash: string;
  readonly currency: typeof SKIN_BANNER_CURRENCY;
  readonly pool: readonly SkinBannerPoolEntry[];
  readonly socketProfileId: string;
  readonly socketProfileVersion: number;
  readonly duplicatePolicy: SkinDuplicatePolicy;
}

/** O banner do Alpha Fechado. Estável: a migration projeta esta id. */
export const CLOSED_ALPHA_BANNER_ID = 'closed_alpha_skin_collection';

/**
 * Every banner this build ships. **One, and it is authored here.**
 *
 * ## Esta constante é a FONTE, e a tabela é a projeção
 *
 * `SkinGachaService` lê `skin_banners` no PostgreSQL, não este array - um
 * servidor não deve precisar de um deploy para uma configuração ser corrigida.
 * Mas a configuração é **decidida aqui**, e a migration que insere a linha é
 * projeção literal desta constante: mesma id, mesma versão, mesmo preço, mesmo
 * pool, mesmos pesos, mesmo fingerprint.
 *
 * Manter as duas à mão, independentes, seria ter dois preços que ninguém
 * comparou. Uma prova de banco recalcula o fingerprint desta constante e exige
 * que a linha carregue o mesmo, então divergir é teste vermelho.
 *
 * ## As decisões que esta configuração É
 *
 * Vinte de Cash por rolagem, só Cash, três skins com peso idêntico, nova
 * instância em duplicata, perfil de sockets v2, sem pity e **sem janela**: nem
 * `startsAt` nem `endsAt`. O Alpha Fechado é uma fase contínua, e uma data de
 * fim inventada seria escassez artificial numa oferta que não tem prazo.
 *
 * Os pesos são 1000/1000/1000 e não 1001/1000/999. Três mil pontos não dividem
 * dez mil basis points por igual, e a chance publicada de cada skin fica em
 * 3333 bps - o que soma 9999. Empurrar um peso para a soma exibida fechar em
 * 100% seria mudar a oferta para arrumar um número na tela.
 *
 * Testes constroem os seus próprios banners como fixtures de banco. Aqueles
 * nunca entram neste array e nunca chegam ao catálogo.
 *
 * ## As anotações `@__PURE__`, e por que elas não são enfeite
 *
 * `Object.freeze` é uma chamada, e um minificador não pode provar que ela não
 * tem efeito colateral - então ele a mantém mesmo quando ninguém usa o
 * resultado. Sem as anotações, esta configuração inteira viajava para o bundle
 * do JOGADOR como resíduo morto: preço, pool e pesos escritos num artefato que
 * a UI não deve consultar, porque quem publica a oferta é a resposta do
 * servidor. O lockdown pegou isso, e é por isso que elas estão aqui.
 */
export const SKIN_BANNERS: readonly SkinBannerDefinition[] = /* @__PURE__ */ Object.freeze([
  /* @__PURE__ */ Object.freeze({
    id: CLOSED_ALPHA_BANNER_ID,
    version: 1,
    status: 'active',
    startsAt: null,
    endsAt: null,
    priceCash: '20',
    currency: SKIN_BANNER_CURRENCY,
    pool: /* @__PURE__ */ Object.freeze([
      /* @__PURE__ */ Object.freeze({ definitionId: 'skin_silver_hunter', weight: 1000 }),
      /* @__PURE__ */ Object.freeze({ definitionId: 'skin_golden_wing', weight: 1000 }),
      /* @__PURE__ */ Object.freeze({ definitionId: 'skin_cyber_rainbow', weight: 1000 }),
    ]),
    socketProfileId: SKIN_SOCKET_PROFILE_ID,
    socketProfileVersion: SKIN_SOCKET_PROFILE_VERSION,
    duplicatePolicy: 'new_instance',
  }),
]);

/** The total weight of a pool. Integer, exact, never zero for a usable banner. */
export function skinBannerTotalWeight(pool: readonly SkinBannerPoolEntry[]): number {
  return pool.reduce((total, entry) => total + entry.weight, 0);
}

/**
 * The definition one uniform point in `[0, totalWeight)` selects.
 *
 * Same construction as the socket bands and for the same reason: contiguous
 * half-open integer bands, no floating point anywhere, and a point outside the
 * domain is an error rather than a clamp.
 */
export function skinBannerEntryAtPoint(
  pool: readonly SkinBannerPoolEntry[],
  point: number,
): SkinBannerPoolEntry {
  const total = skinBannerTotalWeight(pool);
  if (!Number.isSafeInteger(point) || point < 0 || point >= total) {
    throw new RangeError(`banner draw point must be within [0, ${total}), received ${point}`);
  }
  let cursor = 0;
  for (const entry of pool) {
    cursor += entry.weight;
    if (point < cursor) {
      return entry;
    }
  }
  throw new Error(`no pool entry contains point ${point}`);
}

/**
 * The exact chance of one entry, in basis points, rounded DOWN.
 *
 * Display only, and it says so. The draw is decided by the integer weights
 * against the integer total; this is what a screen prints. Rounding down rather
 * than to nearest is deliberate: a published chance must never be higher than
 * the real one.
 */
export function skinBannerChanceBps(
  pool: readonly SkinBannerPoolEntry[],
  entry: SkinBannerPoolEntry,
): number {
  const total = skinBannerTotalWeight(pool);
  return total === 0 ? 0 : Math.floor((entry.weight * 10_000) / total);
}

/**
 * The canonical string a banner's fingerprint is computed from.
 *
 * Deterministic and total: every field that changes what a player is buying is
 * in it, in a fixed order, with the pool sorted by definition id so that two
 * configurations differing only in array order hash the same. Nothing else is
 * in it - no timestamps of when the row was written, no display strings - so a
 * cosmetic edit does not invalidate a fingerprint a client is holding.
 *
 * Hashed with SHA-256 by the server. This function stays pure so that the
 * server and a test compute the same source without either importing crypto.
 */
export function skinBannerFingerprintSource(banner: SkinBannerDefinition): string {
  const pool = [...banner.pool]
    .sort((left, right) => (left.definitionId < right.definitionId ? -1 : 1))
    .map((entry) => `${entry.definitionId}:${entry.weight}`)
    .join(',');
  return [
    `id=${banner.id}`,
    `version=${banner.version}`,
    `status=${banner.status}`,
    `startsAt=${banner.startsAt ?? ''}`,
    `endsAt=${banner.endsAt ?? ''}`,
    `price=${banner.priceCash}`,
    `currency=${banner.currency}`,
    `pool=${pool}`,
    `socketProfile=${banner.socketProfileId}@${banner.socketProfileVersion}`,
    `duplicates=${banner.duplicatePolicy}`,
  ].join('|');
}

/**
 * Why a banner may not be rolled. Structural only - it says nothing about the
 * caller, their balance or their history.
 */
export const SKIN_BANNER_DEFECTS = [
  'not_active',
  'not_started',
  'ended',
  'empty_pool',
  'non_positive_weight',
  'unknown_socket_profile',
  'price_not_positive',
] as const;
export type SkinBannerDefect = (typeof SKIN_BANNER_DEFECTS)[number];

/**
 * Everything wrong with a banner's own configuration, at a given instant.
 *
 * Returns a LIST rather than the first problem, so a misconfigured banner is
 * reported completely rather than one fix at a time. The definitions in the
 * pool are NOT validated here: whether a skin has art, a VFX descriptor and an
 * `enabled` flag is a question about the catalogue, and the server answers it
 * where it can see the catalogue.
 */
export function skinBannerDefects(banner: SkinBannerDefinition, now: Date): SkinBannerDefect[] {
  const defects: SkinBannerDefect[] = [];
  if (banner.status !== 'active') {
    defects.push(banner.status === 'ended' ? 'ended' : 'not_active');
  }
  if (banner.startsAt !== null && Date.parse(banner.startsAt) > now.getTime()) {
    defects.push('not_started');
  }
  if (banner.endsAt !== null && Date.parse(banner.endsAt) <= now.getTime()) {
    defects.push('ended');
  }
  if (banner.pool.length === 0) {
    defects.push('empty_pool');
  }
  if (banner.pool.some((entry) => !Number.isSafeInteger(entry.weight) || entry.weight <= 0)) {
    defects.push('non_positive_weight');
  }
  if (
    banner.socketProfileId !== SKIN_SOCKET_PROFILE_ID ||
    banner.socketProfileVersion !== SKIN_SOCKET_PROFILE_VERSION
  ) {
    defects.push('unknown_socket_profile');
  }
  if (!isPositiveCashAmount(banner.priceCash)) {
    defects.push('price_not_positive');
  }
  return [...new Set(defects)];
}

/**
 * A Cash price on the wire: a decimal string, positive, inside the ledger's
 * 40-digit capacity.
 *
 * A `bigint` rather than a `Number` at every step, and never parsed through
 * one: a price is a currency amount, and this codebase does not let a currency
 * amount become a float even briefly.
 */
export function isPositiveCashAmount(value: string): boolean {
  if (!/^[0-9]{1,40}$/.test(value)) {
    return false;
  }
  if (value.length > CURRENCY_MAX_DIGITS) {
    return false;
  }
  return BigInt(value) > 0n;
}
